import { createAsyncThunk } from "@reduxjs/toolkit";
import api, { apiWithFormDataMethods } from "../../../utils/constants/api";
import { Country, CountryApiResponse, Question, EstablishmentTypeQuestion } from "./lostAndFoundItemRequest";
import { Ship } from "../../../types/ShipTypes";
import { Airport } from "../../../types/AirportTypes";
import { EstablishmentType } from "../../../types/EstablishmentTypes";
import { ILocation, LocationApiResponse } from "../../../types/LocationTypes";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { ApiResponse } from "../../../types/APIResponse";
import { Category, SubCategory } from "../../../types/CategoryTypes";

export const sendLostItemRequest = createAsyncThunk<any, FormData, { rejectValue: string }>("lostItem/sendLostItemRequest", async (formData: FormData, thunkAPI) => {
  try {
    const response: any = await apiWithFormDataMethods.post("/Lost/sendLostRequest", formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to send lost item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const sendFoundItemRequest = createAsyncThunk<any, FormData, { rejectValue: string }>("lostItem/sendFoundItemRequest", async (formData: FormData, thunkAPI) => {
  try {
    const response: any = await apiWithFormDataMethods.post(apiEndpoints.found.sendFoundReport, formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to send found item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getCountries = createAsyncThunk<Country[], void, { rejectValue: string }>("country/getCountries", async (_, thunkAPI) => {
  try {
    const response: CountryApiResponse = await api.get(`/location/getCountries`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch countries");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getShips = createAsyncThunk<Ship[], void, { rejectValue: string }>("ship/getShips", async (_, thunkAPI) => {
  try {
    const response: any = await api.get(`${apiEndpoints.getShip}`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch ships");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getAirports = createAsyncThunk<Airport[], void, { rejectValue: string }>("airport/getAirports", async (_, thunkAPI) => {
  try {
    const response: any = await api.get(`${apiEndpoints.getAirport}`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch ships");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getEstablishmentTypes = createAsyncThunk<EstablishmentType[], void, { rejectValue: string }>("establishmentType/getEstablishmentTypes", async (_, thunkAPI) => {
  try {
    const response: any = await api.get(`${apiEndpoints.getEstablishmentType}`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch ships");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getEstablishmentTypeQuestions = createAsyncThunk<
    { data: EstablishmentTypeQuestion[]; total: number },
    { search_query?: string; offset?: number; limit?: number; establishmentTypeId: string },
    { rejectValue: string }
    >(
    'establishmentTypeQuestion/getEstablishmentTypeQuestions',
    async ({ search_query = '', offset = 0, limit = 1000, establishmentTypeId }, { rejectWithValue }) => {
      try {
        const response: ApiResponse<EstablishmentTypeQuestion[]> & { counts: number } = await api.post(
            apiEndpoints.getEstablishmentTypeQuestion,
            {
              data: {
                search_query,
                offset,
                limit,
                establishmentTypeId,
                order: {
                  "question.createdAt": "ASC",
                  "options.createdAt": "ASC",
                }
              },
            }
        );
        return { data: response.data, total: response.counts };
      } catch (error: any) {
        return rejectWithValue(error?.message || 'Failed to fetch establishmentTypeQuestions');
      }
    }
);

export const getLocations = createAsyncThunk<ILocation[], void, { rejectValue: string }>("location/getLocations", async (_, thunkAPI) => {
  try {
    const response: LocationApiResponse = await api.get(`${apiEndpoints.getLocations}`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch locations");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getItemTypes = createAsyncThunk<any, void, { rejectValue: string }>("itemType/getItemTypes", async (_, thunkAPI) => {
  try {
    const response: any = await api.get(`/itemType/getItemTypes`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch item types");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

// Async Thunks
export const getCategories = createAsyncThunk<Category[], void, { rejectValue: string }>("categories/getCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<ApiResponse<Category[]>>(apiEndpoints.category.getAll, { params: { status: 1 } });
    return response.data; // Access the `data` field containing the categories
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch categories");
  }
});

export const getSubCategories = createAsyncThunk<SubCategory[], void, { rejectValue: string }>("subCategories/getSubCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<ApiResponse<SubCategory[]>>(apiEndpoints.subcategory.getAll, { params: { status: 1, filterEmpty: 1 } });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch subcategories");
  }
});

// Get Questions
export const getQuestions = createAsyncThunk<{ data: Question[]; total: number }, { search_query?: string; offset?: number; limit?: number; subCategoryId: string }, { rejectValue: string }>(
  "questions/getQuestions",
  async ({ search_query = "", offset = 0, limit = 1000, subCategoryId }, { rejectWithValue }) => {
    try {
      const response: ApiResponse<Question[]> & { counts: number } = await api.post(apiEndpoints.question.getAll, {
        data: {
          search_query,
          offset,
          limit,
          subCategoryId,
          order: {
            "question.createdAt": "ASC",
            "options.createdAt": "ASC"
          },
        },
      });
      return { data: response.data, total: response.counts };
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch questions");
    }
  }
);
